import { useEffect, useState } from "react"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"
import { Button } from "../common/button"
import useTranslator from "@pma/hooks/use-translator"
import { Switch } from "../common/switch"
import { Label } from "../common/label"
import { useAuth } from "@pma/stores/auth-store"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import {
  MOVE_TASK_TO_ANOTHER_PROJECT_ENDPOINT,
  PROJECTS_BY_CURRENT_USER_FILTER,
  SERVER_URL,
} from "@pma/utils/api-routes"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { Loader } from "lucide-react"
import { Input } from "../common/input"
import { Separator } from "../common/separator"

interface Props {
  IsModalOpen: boolean
  setIsModalOpen: any
  task?: DictType
  tasks?: DictType[]
  project_id: number
}

function TransferProject({ IsModalOpen, setIsModalOpen, task, tasks, project_id }: Props) {
  const { translate } = useTranslator()
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [userValue, setUserValue] = useState({ with_users: false, targer_project_id: null })
  const [Loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState<string>("")
  const [projectList, setProjectList] = useState([])
  const [renderButtonText, setRenderBtnText] = useState("")

  const getProjectsByCurrentUser = async (orgId: string) => {
    const response = await axios.post(
      SERVER_URL + PROJECTS_BY_CURRENT_USER_FILTER,
      {
        paging_per_page: 100,
        by_organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, isLoading } = useQuery({
    queryKey: ["projects-list", orgId],
    queryFn: () => getProjectsByCurrentUser(orgId),
  })

  const moveTask = async (payload: any) => {
    await axios
      .post(SERVER_URL + MOVE_TASK_TO_ANOTHER_PROJECT_ENDPOINT, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        const { status } = res
        if (status === 200) {
          toast({
            title: translate("Success"),
            description: translate("Moved successfully"),
            variant: "success",
          })
          setLoading(false)
          setIsModalOpen(false)
          queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
          queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
          queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
        }
      })
      .catch((error) => {
        const message = error.response.data.message
        setLoading(false)
        toast({
          title: translate("Error"),
          description: message || translate("Error occurred while moving task"),
          variant: "error",
        })
      })
  }

  const handleBatchMove = async (taskArr: DictType[], payload: any) => {
    const promiseArr: any = []
    taskArr.map((tsk) => {
      payload["task_id"] = tsk.taskId
      const res = axios.post(SERVER_URL + MOVE_TASK_TO_ANOTHER_PROJECT_ENDPOINT, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      promiseArr.push(res)
    })

    Promise.all(promiseArr)
      .then((res) => {
        if (res) {
          toast({
            title: translate("Success"),
            description: translate("Moved successfully"),
            variant: "success",
          })

          setLoading(false)
          setIsModalOpen(false)
          queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
          queryClient.invalidateQueries({ queryKey: ["clear-selected-tasks"] })
          queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
          queryClient.invalidateQueries({ queryKey: ["clear-selected-group-tasks"] })
        }
      })
      .catch((error) => {
        const message = error.response.data.message
        setLoading(false)
        toast({
          title: translate("Error"),
          description: message || translate("Error occurred while moving task"),
          variant: "error",
        })
      })
  }

  const handleSubmit = async () => {
    if (userValue.targer_project_id) {
      setLoading(true)
      if (task) {
        const payload = {
          ...userValue,
          task_id: task.id,
          inform_users: true,
        }
        moveTask(payload)
      } else if (tasks && tasks.length > 0) {
        const payload = {
          ...userValue,
          task_id: null,
          inform_users: true,
        }
        handleBatchMove(tasks, payload)
      }
    } else {
      toast({
        title: translate("Error"),
        description: translate("Please select project"),
        variant: "error",
      })
    }
  }

  const handleChange = (name: string, value: number) => {
    setUserValue((prev) => {
      return { ...prev, [name]: value }
    })
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (data?.items) setProjectList(data?.items?.filter((val: any) => val.id !== project_id))
  }, [data])

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = data?.items
        .filter((project: DictType) =>
          project.title.toLowerCase().includes(searchText.toLowerCase()),
        )
        ?.filter((val: any) => val.id !== project_id)
      setProjectList(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  return (
    <Dialog open={IsModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className='w-max'>
        <DialogHeader>
          <DialogTitle className='py-2'>{translate("Move task to other project")}</DialogTitle>
          <DialogDescription className='flex h-[80px] w-full flex-row justify-evenly gap-5 rounded-md border p-5'>
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger
                  className='outline-none focus:outline-none focus-visible:outline-none'
                  asChild
                >
                  <Button
                    variant={"outline"}
                    size={"sm"}
                    className='w-max outline-none focus:outline-none focus-visible:outline-none'
                  >
                    {userValue.targer_project_id ? renderButtonText : translate("To project")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='start'
                  className='max-h-[250px] min-w-fit !overflow-scroll'
                >
                  <Input
                    value={searchText}
                    placeholder={translate("Search Project")}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Separator className='my-2' />
                  {projectList?.length > 0 ? (
                    projectList.map((val: any) => {
                      return (
                        <DropdownMenuItem
                          onClick={() => {
                            setRenderBtnText(val.title)
                            handleChange("targer_project_id", val.id)
                            setSearchText("")
                          }}
                          className='my-1 flex cursor-pointer flex-row items-center gap-1 rounded-md'
                        >
                          {val.title}
                        </DropdownMenuItem>
                      )
                    })
                  ) : (
                    <p className='text-center'>{translate("No Project")}</p>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Tooltip>
              <TooltipTrigger>
                <div className='flex flex-row items-center gap-3'>
                  <Label className='flex w-full font-semibold'>{translate("With users")}</Label>
                  <Switch
                    checked={userValue.with_users}
                    className='h-6 w-11'
                    name='with_users'
                    onCheckedChange={(checked) => {
                      return setUserValue((prev) => {
                        return { ...prev, with_users: checked }
                      })
                    }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {translate("Select this switch to include assign users too")}
              </TooltipContent>
            </Tooltip>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='flex w-full gap-4'>
          <DialogClose className='w-full'>
            <Button variant={"outline"} className='w-full' size={"default"} onClick={handleCancel}>
              {translate("Cancel")}
            </Button>
          </DialogClose>
          <div className='flex w-full items-center justify-center'>
            <Button
              size={"default"}
              className='w-full'
              variant={"default"}
              disabled={isLoading || Loading}
              onClick={handleSubmit}
            >
              {isLoading || Loading ? <Loader /> : translate("Move")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TransferProject
